import { useState, useEffect, Fragment } from "react";
import { Link } from "react-router-dom";
import { GoogleReviewsBadge } from "@/components/shared/GoogleReviewsBadge";
import { Helmet } from "react-helmet-async";
import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { SEOHead } from "@/features/shared/SEOHead";
import { homeFaqs } from "@/data/home-faqs";
import { generateProfessionalServiceSchema } from "@/lib/business-schema";
import { ChevronDown, Star, ArrowRight, ArrowUpRight } from "lucide-react";
import { useRevealObserver } from "@/hooks/useRevealObserver";

/* True when motion should be skipped: prerender (Puppeteer) or reduced-motion. */
const prefersStatic = () =>
  (typeof navigator !== "undefined" && navigator.webdriver === true) ||
  (typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

/* Animated count-up for the hero metrics; renders the final value in prerender. */
const CountUp = ({ to, decimals = 0, suffix = "" }: { to: number; decimals?: number; suffix?: string }) => {
  const [val, setVal] = useState<number>(() => (prefersStatic() ? to : 0));
  useEffect(() => {
    if (prefersStatic()) return;
    let raf = 0;
    const start = performance.now();
    const dur = 1400;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / dur);
      setVal(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>{val.toFixed(decimals)}{suffix}</>;
};

/* Capability marquee: each item links to its platform/solution page. */
const marqueeItems = [
  { label: "Voice AI", to: "/platform/ai-receptionist/" },
  { label: "AI-first CRM", to: "/solutions/crm-automation/" },
  { label: "Websites", to: "/solutions/websites/" },
  { label: "Reputation", to: "/platform/reputation-management/" },
  { label: "Local SEO", to: "/solutions/local-seo-reputation/" },
  { label: "Ad Management", to: "/solutions/ad-management-scale/" },
  { label: "Email & SMS", to: "/solutions/lead-generation-system/" },
  { label: "Payments", to: "/platform/" },
  { label: "Booking", to: "/platform/speed-to-lead/" },
  { label: "Social Media", to: "/platform/" },
  { label: "Phone Systems", to: "/platform/business-phone-system/" },
  { label: "Analytics", to: "/platform/" },
];

/* Rotating sample events for the hero "live activity" cards. */
const liveFeed = [
  {
    label: "Missed call",
    title: "Auto-answered & texted back",
    sub: "(807) 555-0192 · booked for 4 PM",
  },
  {
    label: "Reputation",
    title: "Review request sent ★★★★★",
    sub: "Job complete · Marcus R.",
  },
  {
    label: "Website chat",
    title: "Chat lead captured & qualified",
    sub: "Sara C. · asked for a quote",
  },
  {
    label: "Booking",
    title: "Appointment booked by AI",
    sub: "Tomorrow 10:30 AM · reminder set",
  },
];

const Index = () => {
  // State for ROI Calculator
  const [missed, setMissed] = useState(6);
  const [value, setValue] = useState(3000);
  const [close, setClose] = useState(25);

  // State for mobile nav menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // State for FAQ accordion
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // Scroll-triggered reveals (below-fold sections)
  useRevealObserver();

  // Hero live-activity feed rotation (paused for prerender / reduced motion)
  const [feedIdx, setFeedIdx] = useState(2);
  useEffect(() => {
    if (prefersStatic()) return;
    const id = window.setInterval(() => setFeedIdx((i) => i + 1), 4200);
    return () => window.clearInterval(id);
  }, []);

  // Calculator computations
  const monthlyLeak = Math.round(missed * 4.3 * value * (close / 100));
  const fmt = (n: number) => "$" + n.toLocaleString("en-CA");

  const valueFmt = fmt(value);
  const monthlyFmt = fmt(monthlyLeak);
  const yearlyFmt = fmt(monthlyLeak * 12);

  // Toggle FAQ item
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const portfolioCompanies = [
    {
      name: "Need A Car",
      logo: (
        <div className="flex items-center gap-2.5 text-[#2F5FE8]">
          <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9C2 11.3 2 11.7 2 12v4c0 .6.4 1 1 1h2" />
            <circle cx="7" cy="17" r="2" />
            <circle cx="17" cy="17" r="2" />
          </svg>
          <span className="font-mono-ibm font-extrabold text-[15px] tracking-tighter text-[#101319]">NEED A CAR</span>
        </div>
      )
    },
    {
      name: "Global Aluminum",
      logo: <img src="/logos/clients/global-aluminum.webp" alt="Global Aluminum" width={226} height={96} loading="lazy" className="h-8 w-auto object-contain" />
    },
    {
      name: "Centennial Foods",
      logo: <img src="/logos/clients/centennial-foods.svg" alt="Centennial Family Foods" width={280} height={120} loading="lazy" className="h-8 w-auto object-contain" />
    },
    {
      name: "Monty's Joint",
      logo: <img src="/logos/clients/montys-joint.webp" alt="Monty's Joint" width={373} height={96} loading="lazy" className="h-8 w-auto object-contain" />
    },
    {
      name: "NearingPoint",
      logo: (
        <div className="flex items-center gap-2 text-[#2F5FE8]">
          <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="4" />
          </svg>
          <span className="font-mono-ibm font-medium text-xs tracking-[0.12em] text-[#101319] uppercase">NEARINGPOINT</span>
        </div>
      )
    },
    {
      name: "Kovara",
      logo: (
        <div className="flex items-center gap-2 text-[#2F5FE8]">
          <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span className="font-sans font-black text-sm tracking-[0.1em] text-[#101319] uppercase">KOVARA</span>
        </div>
      )
    },
    {
      name: "Gordon Trailer",
      logo: <img src="/logos/clients/gordon-trailer.webp" alt="Gordon Trailer Sales" width={185} height={96} loading="lazy" className="h-9 w-auto object-contain" />
    },
    {
      name: "Urbis Concepts",
      logo: (
        <div className="flex items-center gap-2 text-[#2F5FE8]">
          <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
          </svg>
          <span className="font-sans font-extrabold text-xs tracking-[0.15em] text-[#101319] uppercase">URBIS CONCEPTS</span>
        </div>
      )
    },
    {
      name: "The Motoring District",
      logo: <img src="/logos/clients/motoring-district.webp" alt="The Motoring District" width={251} height={96} loading="lazy" className="h-9 w-auto object-contain" />
    },
    {
      name: "JC Training & Development",
      logo: <img src="/logos/clients/jc-training.svg" alt="JC Training & Development" width={430} height={120} loading="lazy" className="h-8 w-auto object-contain" />
    },
    {
      name: "South Core Roar",
      logo: <img src="/logos/clients/south-core-roar.webp" alt="South Core Roar" width={130} height={96} loading="lazy" className="h-10 w-auto object-contain" />
    },
    {
      name: "FWBIA",
      logo: <img src="/logos/clients/fwbia.webp" alt="Fort William BIA" width={243} height={96} loading="lazy" className="h-8 w-auto object-contain" />
    },
    {
      name: "MyWai",
      logo: <img src="/logos/clients/mywai.webp" alt="MyWai" width={310} height={96} loading="lazy" className="h-7 w-auto object-contain" />
    },
    {
      name: "Parma Car",
      logo: <img src="/logos/clients/parma-car.webp" alt="Parma Car" width={125} height={96} loading="lazy" className="h-10 w-auto object-contain" />
    },
    {
      name: "Eagle Eye Herbs",
      logo: <img src="/logos/clients/eagle-eye-herbs.webp" alt="Eagle Eye Herbs" width={96} height={96} loading="lazy" className="h-10 w-auto object-contain" />
    },
    {
      name: "Thunder Bay AI",
      logo: (
        <div className="flex items-center gap-2 text-[#2F5FE8]">
          <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
            <circle cx="12" cy="12" r="3.5" />
          </svg>
          <span className="font-mono-ibm font-bold text-xs tracking-[0.14em] text-[#101319] uppercase">THUNDER BAY AI</span>
        </div>
      )
    }
  ];



  return (
    <div className="min-h-screen bg-[#FBFAF7] text-[#101319] font-sans antialiased overflow-x-hidden relative selection:bg-[#2F5FE8] selection:text-white">
      {/* Redesign Styles & Fonts */}
      <Helmet>
        {/* Fonts are self-hosted globally (index.css @font-face + .font-instrument/.font-mono-ibm) */}
        <link rel="preload" as="image" href="/hero-abstract-lines.webp" />
        {/* LocalBusiness schema: NAP mirrors the GBP listing exactly (street address
            published by Denis 2026-07-18); footer carries the same string. */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "@id": "https://frayze.ca/#business",
          "name": "Frayze Technologies Inc.",
          "url": "https://frayze.ca/",
          "logo": "https://frayze.ca/images/frayze-logo-dark.webp",
          "telephone": "+1-807-700-0079",
          "email": "hello@frayze.ca",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "202 Franklin St. North",
            "addressLocality": "Thunder Bay",
            "addressRegion": "ON",
            "postalCode": "P7C 4H9",
            "addressCountry": "CA"
          },
          "areaServed": ["Thunder Bay", "Northern Ontario", "Ontario", "Canada"],
          "description": "Managed websites, CRM, and AI lead automation for lead-driven businesses: web design, local SEO, AI receptionists, and follow-up systems, built and run for you.",
          "sameAs": [
            "https://facebook.com/frayze.ca",
            "https://instagram.com/frayze.ca",
            "https://www.linkedin.com/company/frayze/",
            "https://x.com/FrayzeIncCanada",
            "https://youtube.com/@frayzetech"
          ]
        })}</script>
        <style>{`
          /* Keyframe Animations */
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(26px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes marquee {
            from { transform: translateX(0); }
            to { transform: translateX(-50%); }
          }
          @keyframes drawLine {
            from { stroke-dashoffset: 600; }
            to { stroke-dashoffset: 0; }
          }
          @keyframes pulseDot {
            0% { box-shadow: 0 0 0 0 rgba(47,95,232,0.45); }
            70% { box-shadow: 0 0 0 12px rgba(47,95,232,0); }
            100% { box-shadow: 0 0 0 0 rgba(47,95,232,0); }
          }
          @keyframes floatY {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-8px); }
          }
          @keyframes barGrow {
            from { transform: scaleY(0.15); }
            to { transform: scaleY(1); }
          }
          @keyframes wave {
            0%, 100% { transform: scaleY(0.35); }
            50% { transform: scaleY(1); }
          }
          @keyframes ticker {
            0%, 28% { opacity: 1; transform: translateY(0); }
            33%, 95% { opacity: 0; transform: translateY(-8px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          /* Animation classes */
          .animate-fadeUp {
            animation: fadeUp 0.7s cubic-bezier(0.2,0.8,0.2,1) both;
          }
          .animate-marquee {
            animation: marquee 36s linear infinite;
          }
          .animate-drawLine {
            animation: drawLine 3.2s cubic-bezier(0.4,0,0.2,1) both;
          }
          .animate-pulseDot {
            animation: pulseDot 2.4s infinite;
          }
          .animate-floatY {
            animation: floatY 5s ease-in-out infinite;
          }
          .animate-barGrow {
            animation: barGrow 0.9s cubic-bezier(0.2,0.8,0.2,1) both;
          }
          .animate-wave {
            animation: wave 1.3s ease-in-out infinite;
          }
          /* Marquee items are links: stroke text fills brand blue on hover; track pauses */
          .marquee-link {
            -webkit-text-stroke: 1.2px rgba(255, 255, 255, 0.9);
            -webkit-text-fill-color: transparent;
            transition: -webkit-text-fill-color 0.3s ease, -webkit-text-stroke-color 0.3s ease;
          }
          .marquee-link:hover, .marquee-link:focus-visible {
            -webkit-text-fill-color: #2F5FE8;
            -webkit-text-stroke-color: #2F5FE8;
          }
          .marquee-wrap:hover .animate-marquee {
            animation-play-state: paused;
          }
          /* Ambient hero orbs: slow drift, Autocorp-style but on cream */
          @keyframes orbDriftA {
            0%, 100% { transform: translate(0, 0) scale(1); }
            33% { transform: translate(48px, -36px) scale(1.06); }
            66% { transform: translate(-24px, 28px) scale(0.97); }
          }
          @keyframes orbDriftB {
            0%, 100% { transform: translate(0, 0) scale(1); }
            40% { transform: translate(-52px, -30px) scale(1.05); }
            75% { transform: translate(30px, 24px) scale(0.96); }
          }
          .animate-orbA { animation: orbDriftA 26s ease-in-out infinite; }
          .animate-orbB { animation: orbDriftB 34s ease-in-out infinite; }
          /* Pulse traveling the customer-journey line */
          @keyframes journeyDot {
            0% { left: 1%; opacity: 0; }
            8% { opacity: 1; }
            88% { opacity: 1; }
            100% { left: 97%; opacity: 0; }
          }
          .animate-journeyDot {
            opacity: 0;
            animation: journeyDot 6.5s cubic-bezier(0.45, 0, 0.25, 1) infinite 1.4s;
          }
          @media (prefers-reduced-motion: reduce) {
            .animate-fadeUp, .animate-marquee, .animate-drawLine, .animate-pulseDot,
            .animate-floatY, .animate-barGrow, .animate-wave,
            .animate-journeyDot, .animate-orbA, .animate-orbB {
              animation: none;
            }
          }
        `}</style>
      </Helmet>

      {/* SEO Configuration (100% Preserved) */}
      <SEOHead
        title="Canadian AI Automation, Websites, CRM & Leads | Frayze"
        description="AI automation, websites, CRM & lead systems for Canadian lead-driven businesses: auto dealers, trades & local service operators. Built in Thunder Bay. One connected system that catches every lead. Book a free call."
        keywords="AI automation, websites, CRM automation, lead systems, automotive CRM, Canadian web design, lead capture, business automation, Canadian digital agency"
        ogTitle="Canadian AI Automation, Websites, CRM & Leads | Frayze"
        ogDescription="AI automation, websites, CRM & lead systems for Canadian lead-driven businesses: auto dealers, trades & local service operators. Built in Thunder Bay. One connected system that catches every lead. Book a free call."
        twitterTitle="Canadian AI Automation, Websites, CRM & Leads | Frayze"
        twitterDescription="AI automation, websites, CRM & lead systems for Canadian lead-driven businesses: auto dealers, trades & local service operators. Built in Thunder Bay. One connected system that catches every lead. Book a free call."
        canonical="https://frayze.ca/"
        robots="index, follow, max-snippet:-1, max-image-preview:large"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            generateProfessionalServiceSchema(),
            {
              "@type": "Product",
              "name": "7-Day Website Launch Program",
              "description": "Get a professional website and AI business system built for your business.",
              "brand": {
                "@type": "Brand",
                "name": "Frayze"
              },
              "offers": {
                "@type": "AggregateOffer",
                "url": "https://frayze.ca/pricing/",
                "priceCurrency": "CAD",
                "lowPrice": "197.00",
                "highPrice": "997.00",
                "offerCount": 2,
                "availability": "https://schema.org/InStock",
                "priceValidUntil": "2026-12-31"
              }
            },
            {
              "@type": "FAQPage",
              "mainEntity": homeFaqs.map((faq) => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            }
          ]
        }}
      />
      <Header />

      <main>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-[#FBFAF7] lg:h-screen lg:min-h-[780px] flex items-center pt-36 pb-16 lg:py-0">
        {/* Background Image layer with Professional design treatment */}
        <div className="absolute inset-0 pointer-events-none select-none">
          <img 
            src="/hero-abstract-lines.webp"
            alt="Abstract blue lines background" 
            className="hidden lg:block w-full h-full lg:object-contain object-left-top lg:scale-x-[-1] opacity-100" 
            fetchPriority="high"
          />
          
          
          {/* Ambient drift orbs: slow, subliminal movement behind the content */}
          <div className="absolute -top-40 -left-40 w-[42rem] h-[42rem] rounded-full bg-[radial-gradient(circle,rgba(47,95,232,0.10),transparent_65%)] blur-3xl animate-orbA" />
          <div className="absolute -bottom-48 right-[-12%] w-[38rem] h-[38rem] rounded-full bg-[radial-gradient(circle,rgba(47,95,232,0.07),transparent_65%)] blur-3xl animate-orbB" />

          {/* Vignettes for header/marquee blending */}
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#FBFAF7] to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-[#FBFAF7] to-transparent" />
          
          {/* Subtle Grid overlay */}
          <div className="absolute inset-0 bg-grid-fade opacity-[0.2]" />
        </div>

        <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center box-sizing-border font-instrument">
        <div className="lg:col-span-6 flex flex-col gap-6 text-left">
          <div className="inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8] animate-fadeUp">
            <span className="w-6 h-[1px] bg-[#2F5FE8]"></span>
            AI-first · Built in Thunder Bay
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-[68px] lg:text-[72px] leading-[1.02] tracking-[-0.04em] font-semibold text-[#101319] text-pretty animate-fadeUp animation-delay-200">
            Every lead. Every call. Every review. <span className="text-[#2F5FE8] font-bold">Handled.</span>
          </h1>

          <p className="text-lg md:text-xl leading-relaxed text-[#5A6270] max-w-xl text-pretty animate-fadeUp animation-delay-300">
            Frayze is the AI-first platform for Canadian businesses. Website, CRM, reputation, marketing, and communications in one connected system. Start with a modern site.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4 mt-2 animate-fadeUp animation-delay-400">
            <Link to="/website-launch-program" className="w-full sm:w-auto text-center bg-[#2F5FE8] hover:bg-[#2049BE] text-white font-bold px-8 py-4 rounded-xl shadow-lg shadow-[#2F5FE8]/25 transition-all hover:scale-[1.02] active:scale-[0.98]">
              View Launch Program
            </Link>
            <Link to="/book-consultation" className="w-full sm:w-auto text-center border border-[#D8D4C9] bg-white hover:border-[#101319] text-[#101319] font-bold px-8 py-4 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]">
              Book a Free Call
            </Link>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[#5A6270] font-medium mt-2 animate-fadeUp animation-delay-500">
            <span>No contracts</span>
            <span className="w-1 h-1 rounded-full bg-[#C9C4B8]"></span>
            <span>Start with a modern site</span>
            <span className="w-1 h-1 rounded-full bg-[#C9C4B8]"></span>
            <span>Don't pay until it's live</span>
          </div>

          <GoogleReviewsBadge className="animate-fadeUp animation-delay-500" />
        </div>

        {/* System visual */}
        <div className="lg:col-span-6 w-full animate-fadeUp animation-delay-300">
          <div className="bg-white/40 backdrop-blur-xl border border-white/30 rounded-2xl shadow-2xl shadow-[#101319]/5 p-6 md:p-8 flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-0.5">
                <div className="font-bold text-[15px] md:text-base tracking-[-0.01em]">Your business, connected</div>
                <div className="text-xs md:text-[13px] text-[#5A6270]">Inside a Frayze system (sample view)</div>
              </div>
              <div className="flex items-center gap-1.5 font-mono-ibm text-[10px] md:text-xs tracking-[0.12em] uppercase text-[#127A50]">
                <span className="w-2.5 h-2.5 rounded-full bg-[#1B9E6B] animate-pulseDot"></span>
                Demo
              </div>
            </div>

            {/* journey */}
            <div className="relative py-2 overflow-hidden select-none">
              <svg width="100%" height="46" viewBox="0 0 520 46" className="block w-full">
                <path d="M10 23 H510" stroke="#E9E6DE" strokeWidth="2" fill="none"></path>
                <path d="M10 23 H510" stroke="#2F5FE8" strokeWidth="2" fill="none" strokeDasharray="600" className="animate-drawLine"></path>
              </svg>
              {/* pulse traveling the line: a lead moving through the system */}
              <div className="absolute top-1/2 -mt-[5px] w-2.5 h-2.5 rounded-full bg-[#2F5FE8] shadow-[0_0_10px_rgba(47,95,232,0.65)] animate-journeyDot" />
              <div className="absolute inset-0 flex justify-between items-center text-[10px] sm:text-xs md:text-[12.5px] font-bold">
                <span className="bg-[#101319] text-white px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-full whitespace-nowrap shadow-sm">Lead comes in</span>
                <span className="bg-[#2F5FE8] text-white px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-full whitespace-nowrap shadow-sm">AI replies in 8s</span>
                <span className="bg-white border-2 border-[#2F5FE8] text-[#2F5FE8] px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-full whitespace-nowrap shadow-sm">Booked</span>
                <span className="bg-white border border-[#E9E6DE] text-[#101319] px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-full whitespace-nowrap shadow-sm">Reviewed ★</span>
              </div>
            </div>

            {/* live activity: stacked stream, newest event slides in on top */}
            <div className="flex flex-col gap-2">
              {[0, 1, 2].map((offset) => {
                const evNum = feedIdx - offset;
                const ev = liveFeed[((evNum % liveFeed.length) + liveFeed.length) % liveFeed.length];
                return (
                  <div
                    key={evNum}
                    className={`bg-white/30 border border-[#E9E6DE]/60 rounded-xl px-3.5 py-2.5 flex items-center gap-3 ${offset === 0 ? "animate-fadeUp" : ""}`}
                    style={offset === 0 ? { animationDuration: "0.5s" } : undefined}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${offset === 0 ? "bg-[#2F5FE8] animate-pulseDot" : "bg-[#C9C4B8]"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-[13px] font-bold text-[#101319] truncate">{ev.title}</span>
                        <span className="font-mono-ibm text-[9px] tracking-[0.1em] uppercase text-[#5A6270] shrink-0">{ev.label}</span>
                      </div>
                      <div className="text-[11.5px] text-[#5A6270] truncate">{ev.sub}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* metrics display */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/40 border border-[#E9E6DE]/30 rounded-xl p-3.5 flex flex-col gap-0.5 text-center sm:text-left">
                <div className="text-xl sm:text-2xl md:text-[26px] font-bold tracking-tight"><CountUp to={127} /></div>
                <div className="text-[10px] md:text-[11px] text-[#5A6270] font-semibold">leads this month</div>
              </div>
              <div className="bg-white/40 border border-[#E9E6DE]/30 rounded-xl p-3.5 flex flex-col gap-0.5 text-center sm:text-left">
                <div className="text-xl sm:text-2xl md:text-[26px] font-bold tracking-tight"><CountUp to={8} suffix="s" /></div>
                <div className="text-[10px] md:text-[11px] text-[#5A6270] font-semibold">avg. response</div>
              </div>
              <div className="bg-white/40 border border-[#E9E6DE]/30 rounded-xl p-3.5 flex flex-col gap-0.5 text-center sm:text-left">
                <div className="text-xl sm:text-2xl md:text-[26px] font-bold tracking-tight"><CountUp to={4.9} decimals={1} suffix="★" /></div>
                <div className="text-[10px] md:text-[11px] text-[#5A6270] font-semibold">avg. rating kept</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </section>

      {/* ============ CAPABILITY MARQUEE ============ */}
      <section className="bg-[#101319] py-7 md:py-9 overflow-hidden select-none border-y border-[#101319]">
        <div className="flex w-max marquee-wrap">
          {[0, 1].map((copy) => (
            <div
              key={copy}
              aria-hidden={copy === 1}
              className="flex items-center gap-16 md:gap-24 px-8 font-instrument text-xl md:text-3xl lg:text-4xl font-black uppercase tracking-[0.18em] whitespace-nowrap animate-marquee"
            >
              {marqueeItems.map((item) => (
                <Fragment key={item.label}>
                  <Link to={item.to} className="marquee-link" tabIndex={copy === 1 ? -1 : undefined}>
                    {item.label}
                  </Link>
                  <span className="text-[#2F5FE8]">·</span>
                </Fragment>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ============ FULL SUITE BENTO ============ */}
      <section id="suite" className="max-w-[1440px] mx-auto px-6 py-16 md:py-28 flex flex-col gap-14 font-instrument scroll-mt-20">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div className="flex flex-col gap-4 max-w-2xl text-left">
            <div className="reveal inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8]">
              <span className="w-6 h-[1px] bg-[#2F5FE8]"></span>
              The full suite
            </div>
            <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold text-[#101319]">
              One platform for the systems that run your business
            </h2>
          </div>
          <p className="reveal reveal-delay-2 text-base md:text-[17px] leading-relaxed text-[#5A6270] max-w-xs text-left">
            Not a bundle of tools with a login. One connected system, with a team that actually runs it.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-5 auto-rows-[auto]">
          {/* Card 1: Websites (wide) */}
          <div className="reveal lg:col-span-3 bg-white border border-[#E9E6DE] rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row gap-6 overflow-hidden bento-card transition-all duration-300 lift-hover">
            <div className="flex flex-col gap-2.5 text-left min-w-[200px] flex-1">
              <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#2F5FE8]">Websites</div>
              <h3 className="text-xl md:text-[22px] font-bold tracking-[-0.02em] leading-tight">High-performance sites, built for conversion</h3>
              <p className="text-sm md:text-[14.5px] leading-relaxed text-[#5A6270]">Conversion-focused, mobile-first, local-SEO built in.</p>
            </div>
            
            <div className="w-full sm:w-[200px] bg-[#F4F2EC] rounded-xl p-4 flex flex-col gap-3 shrink-0">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#E9E6DE]"></span>
                <span className="w-2 h-2 rounded-full bg-[#E9E6DE]"></span>
                <span className="flex-1 h-[18px] bg-white border border-[#E9E6DE]/60 rounded text-[9.5px] text-[#5A6270] font-mono-ibm flex items-center px-1.5 select-none">yourbusiness.ca</span>
              </div>
              <div className="bg-white rounded-lg p-3 flex-1 flex flex-col gap-2 min-h-[70px]">
                <div className="w-[70%] h-2.5 bg-[#101319] rounded"></div>
                <div className="w-[50%] h-2 bg-[#E9E6DE] rounded"></div>
                <div className="flex gap-1.5 mt-auto">
                  <div className="w-14 h-4.5 bg-[#2F5FE8] rounded"></div>
                  <div className="w-14 h-4.5 border border-[#E9E6DE] rounded"></div>
                </div>
              </div>
              <div className="flex justify-between font-mono-ibm text-[9.5px] text-[#127A50] font-bold">
                <span>Speed 98</span>
                <span>SEO 100</span>
                <span>Mobile-ready</span>
              </div>
            </div>
          </div>

          {/* Card 2: AI-first CRM (wide) */}
          <div className="reveal reveal-delay-1 lg:col-span-3 bg-[#101319] text-[#F2F3F5] rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row gap-6 overflow-hidden bento-card transition-all duration-300 lift-hover">
            <div className="flex flex-col gap-2.5 text-left min-w-[200px] flex-1">
              <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#7C9EFF]">AI-first CRM</div>
              <h3 className="text-xl md:text-[22px] font-bold tracking-[-0.02em] leading-tight text-white">A pipeline that moves itself</h3>
              <p className="text-sm md:text-[14.5px] leading-relaxed text-[#9BA3B2]">Every lead captured, qualified, and followed up automatically.</p>
            </div>

            <div className="w-full sm:w-[200px] grid grid-cols-3 gap-2.5 shrink-0 select-none">
              <div className="bg-white/5 rounded-lg p-2.5 flex flex-col gap-2.5">
                <div className="font-mono-ibm text-[9px] tracking-[0.1em] uppercase text-[#6B7484]">New</div>
                <div className="bg-white/10 rounded-md h-7"></div>
                <div className="bg-white/10 rounded-md h-7"></div>
              </div>
              <div className="bg-white/5 rounded-lg p-2.5 flex flex-col gap-2.5">
                <div className="font-mono-ibm text-[9px] tracking-[0.1em] uppercase text-[#6B7484]">Nurturing</div>
                <div className="bg-[#7C9EFF]/25 border border-[#7C9EFF]/40 rounded-md h-7"></div>
                <div className="bg-white/10 rounded-md h-7"></div>
              </div>
              <div className="bg-white/5 rounded-lg p-2.5 flex flex-col gap-2.5">
                <div className="font-mono-ibm text-[9px] tracking-[0.1em] uppercase text-[#6B7484]">Booked</div>
                <div className="bg-[#1B9E6B]/30 border border-[#1B9E6B]/50 rounded-md h-7"></div>
              </div>
            </div>
          </div>

          {/* Card 3: Communications */}
          <div className="reveal reveal-delay-2 md:col-span-1 lg:col-span-2 bg-white border border-[#E9E6DE] rounded-2xl p-6 md:p-7 flex flex-col gap-4 overflow-hidden bento-card transition-all duration-300 lift-hover">
            <div className="flex flex-col gap-1.5 text-left">
              <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#2F5FE8]">Communications</div>
              <h3 className="text-lg md:text-xl font-bold tracking-[-0.02em] leading-tight">Every channel, one inbox</h3>
            </div>
            
            <div className="flex flex-col gap-2 mt-1 flex-1 text-left">
              <div className="flex items-center gap-3 bg-[#F4F2EC] rounded-lg p-2.5">
                <span className="text-[10px] font-bold text-[#2F5FE8] font-mono-ibm shrink-0">SMS</span>
                <span className="text-xs md:text-[13px] text-[#5A6270] truncate">"Can you come Thursday?"</span>
              </div>
              <div className="flex items-center gap-3 bg-[#F4F2EC] rounded-lg p-2.5">
                <span className="text-[10px] font-bold text-[#2F5FE8] font-mono-ibm shrink-0">CALL</span>
                <span className="text-xs md:text-[13px] text-[#5A6270] truncate">Voicemail transcribed</span>
              </div>
              <div className="flex items-center gap-3 bg-[#F4F2EC] rounded-lg p-2.5">
                <span className="text-[10px] font-bold text-[#2F5FE8] font-mono-ibm shrink-0">EMAIL</span>
                <span className="text-xs md:text-[13px] text-[#5A6270] truncate">Quote follow-up sent · day 3</span>
              </div>
            </div>
          </div>

          {/* Card 4: Reputation */}
          <div className="reveal reveal-delay-3 md:col-span-1 lg:col-span-2 bg-white border border-[#E9E6DE] rounded-2xl p-6 md:p-7 flex flex-col gap-4 overflow-hidden bento-card transition-all duration-300 lift-hover">
            <div className="flex flex-col gap-1.5 text-left">
              <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#2F5FE8]">Reputation</div>
              <h3 className="text-lg md:text-xl font-bold tracking-[-0.02em] leading-tight">5-star reviews on autopilot</h3>
            </div>

            <div className="bg-[#F4F2EC] rounded-xl p-4 flex flex-col gap-2 text-left flex-1">
              <div className="flex text-amber-500 text-sm gap-0.5 tracking-wider">
                <Star className="w-3.5 h-3.5 fill-current" />
                <Star className="w-3.5 h-3.5 fill-current" />
                <Star className="w-3.5 h-3.5 fill-current" />
                <Star className="w-3.5 h-3.5 fill-current" />
                <Star className="w-3.5 h-3.5 fill-current" />
              </div>
              <p className="text-[12.5px] leading-snug text-[#5A6270] italic">"Fast, professional, and follow-up was instant."</p>
              <div className="text-[11px] text-[#5A6270] font-semibold mt-1">Requested automatically · job complete</div>
            </div>
            <div className="text-xs text-[#5A6270] text-left">Asked at the right moment, every time.</div>
          </div>

          {/* Card 5: Marketing */}
          <div className="reveal md:col-span-1 lg:col-span-2 bg-white border border-[#E9E6DE] rounded-2xl p-6 md:p-7 flex flex-col gap-4 overflow-hidden bento-card transition-all duration-300 lift-hover">
            <div className="flex flex-col gap-1.5 text-left">
              <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#2F5FE8]">Marketing &amp; Ads</div>
              <h3 className="text-lg md:text-xl font-bold tracking-[-0.02em] leading-tight">Spend that answers to revenue</h3>
            </div>

            <div className="flex items-end gap-1.5 h-20 pt-2 px-1 select-none flex-grow">
              <div className="flex-1 h-[34%] bg-[#E9E6DE] rounded-t-md animate-barGrow"></div>
              <div className="flex-1 h-[48%] bg-[#E9E6DE] rounded-t-md animate-barGrow animation-delay-200"></div>
              <div className="flex-1 h-[42%] bg-[#E9E6DE] rounded-t-md animate-barGrow animation-delay-300"></div>
              <div className="flex-1 h-[64%] bg-[#A9BFF7] rounded-t-md animate-barGrow animation-delay-400"></div>
              <div className="flex-1 h-[78%] bg-[#6E92F0] rounded-t-md animate-barGrow animation-delay-500"></div>
              <div className="flex-1 h-full bg-[#2F5FE8] rounded-t-md animate-barGrow animation-delay-600"></div>
            </div>
            
            <div className="flex justify-between font-mono-ibm text-[11px] text-[#5A6270] font-bold">
              <span>Ad-to-call tracking</span>
              <span className="text-[#127A50]">to booked revenue</span>
            </div>
          </div>

          {/* Card 6: Voice AI (wide bottom) */}
          <div className="reveal reveal-delay-1 lg:col-span-6 bg-gradient-to-br from-[#EEF2FE] to-[#FBFAF7] border border-[#DCE3F8] rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between bento-card transition-all duration-300 lift-hover">
            <div className="flex flex-col gap-2.5 text-left max-w-sm">
              <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#2F5FE8]">Frayze Reception</div>
              <h3 className="text-xl md:text-2xl font-bold tracking-[-0.02em] leading-tight">A receptionist that never sleeps</h3>
              <p className="text-sm md:text-[14.5px] leading-relaxed text-[#5A6270]">Frayze Reception answers, qualifies, and books, day or night. Managed for you from $197/month. Call the live demo and hear it yourself.</p>
              <Link to="/platform/ai-receptionist/" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#2F5FE8] hover:text-[#2049BE] transition-colors">
                See Frayze Reception
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* wave pulses */}
            <div className="flex items-center gap-1.5 h-12 w-full md:w-auto md:flex-1 max-w-[240px] select-none py-1">
              <span className="flex-1 h-[40%] bg-[#2F5FE8] rounded-full animate-wave"></span>
              <span className="flex-1 h-[70%] bg-[#2F5FE8] rounded-full animate-wave animation-delay-200"></span>
              <span className="flex-1 h-full bg-[#2F5FE8] rounded-full animate-wave animation-delay-300"></span>
              <span className="flex-1 h-[60%] bg-[#6E92F0] rounded-full animate-wave animation-delay-400"></span>
              <span className="flex-1 h-[85%] bg-[#6E92F0] rounded-full animate-wave animation-delay-500"></span>
              <span className="flex-1 h-[45%] bg-[#A9BFF7] rounded-full animate-wave animation-delay-600"></span>
              <span className="flex-1 h-[75%] bg-[#A9BFF7] rounded-full animate-wave animation-delay-200"></span>
              <span className="flex-1 h-[55%] bg-[#DCE3F8] rounded-full animate-wave animation-delay-400"></span>
            </div>

            <a href="tel:+14374949095" className="flex flex-col gap-0.5 bg-white border border-[#DCE3F8] hover:border-[#2F5FE8] rounded-xl p-4 md:px-6 w-full md:w-auto text-left transition-all hover:scale-[1.02] shrink-0">
              <span className="font-mono-ibm text-[10px] tracking-[0.12em] uppercase text-[#5A6270]">Call the demo</span>
              <span className="text-lg md:text-xl font-bold tracking-tight text-[#2F5FE8]">437-494-9095</span>
            </a>
          </div>
        </div>
      </section>

      {/* ============ PORTFOLIO SECTION ============ */}
      <section id="work" className="bg-white border-t border-[#E9E6DE] py-16 md:py-28 font-instrument overflow-hidden">
        <div className="max-w-[1312px] mx-auto px-6 flex flex-col gap-12 text-left">
          {/* Header */}
          <div className="flex flex-col gap-4 max-w-2xl">
            <div className="reveal inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8]">
              <span className="w-6 h-[1px] bg-[#2F5FE8]"></span>
              Proven results
            </div>
            <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold text-[#101319]">
              Featured projects we've built
            </h2>
            <p className="reveal reveal-delay-2 text-base md:text-lg leading-relaxed text-[#5A6270]">
              From custom automotive dealer platforms to local grocer systems, we design and scale bespoke digital solutions.
            </p>
          </div>

          {/* Infinite Client Logos Marquee */}
          <div className="w-full relative overflow-hidden py-6 select-none bg-[#FBFAF7]/50 border-y border-[#E9E6DE]/60 my-2">
            {/* Fades */}
            <div className="absolute left-0 inset-y-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 inset-y-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
            
            <div className="flex w-max items-center" aria-hidden="true">
              <div className="flex items-center gap-20 px-10 animate-marquee whitespace-nowrap">
                {portfolioCompanies.map((company, i) => (
                  <div key={i} className="h-12 flex items-center justify-center shrink-0 filter grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300">
                    {company.logo}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-20 px-10 animate-marquee whitespace-nowrap">
                {portfolioCompanies.map((company, i) => (
                  <div key={`dup-${i}`} className="h-12 flex items-center justify-center shrink-0 filter grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300">
                    {company.logo}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Project Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
            {/* Project 1: Need A Car */}
            <div className="reveal group bg-white border border-[#E9E6DE] rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-[#2F5FE8]/5 hover:border-[#2F5FE8]/50 transition-all duration-300 flex flex-col justify-between lift-hover">
              <div className="p-6 md:p-8 flex flex-col gap-4 text-left">
                <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#2F5FE8]">Founder Build · Automotive</div>
                <h3 className="text-xl md:text-2xl font-bold tracking-tight text-[#101319] group-hover:text-[#2F5FE8] transition-colors">Need A Car</h3>
                <p className="text-sm md:text-[14.5px] leading-relaxed text-[#5A6270]">
                  Before Frayze, the founder built the dealer systems, inventory feed syndication, and missed call recovery across 6 locations.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {['6 Locations', 'AI Systems', 'CRM Sync', 'Syndication'].map((tag) => (
                    <span key={tag} className="text-[10px] md:text-[11px] font-mono-ibm bg-[#FBFAF7] border border-[#E9E6DE] px-2.5 py-1 rounded-full text-[#5A6270]">{tag}</span>
                  ))}
                </div>
              </div>
              <div className="px-6 md:px-8 pb-6 md:pb-8 flex justify-between items-center border-t border-[#E9E6DE]/60 pt-4 bg-[#FBFAF7]/40">
                <span className="text-xs font-bold text-[#127A50]">$1.7M Marketing Savings</span>
                <Link to="/case-studies/need-a-car-automotive-growth" className="text-xs font-bold text-[#2F5FE8] hover:text-[#2049BE] inline-flex items-center gap-1">
                  View Study <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>

            {/* Project 2: Global Aluminum */}
            <div className="reveal reveal-delay-1 group bg-white border border-[#E9E6DE] rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-[#2F5FE8]/5 hover:border-[#2F5FE8]/50 transition-all duration-300 flex flex-col justify-between lift-hover">
              <div className="p-6 md:p-8 flex flex-col gap-4 text-left">
                <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#2F5FE8]">Industrial Systems</div>
                <h3 className="text-xl md:text-2xl font-bold tracking-tight text-[#101319] group-hover:text-[#2F5FE8] transition-colors">Global Aluminum</h3>
                <p className="text-sm md:text-[14.5px] leading-relaxed text-[#5A6270]">
                  High-performance lead generation and CRM automation for high-ticket contract work.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {['Quotes & Pipelines', 'Website', 'Lead Gen', 'Ad Management'].map((tag) => (
                    <span key={tag} className="text-[10px] md:text-[11px] font-mono-ibm bg-[#FBFAF7] border border-[#E9E6DE] px-2.5 py-1 rounded-full text-[#5A6270]">{tag}</span>
                  ))}
                </div>
              </div>
              <div className="px-6 md:px-8 pb-6 md:pb-8 flex justify-between items-center border-t border-[#E9E6DE]/60 pt-4 bg-[#FBFAF7]/40">
                <span className="text-xs font-bold text-[#127A50]">Booked-quote rate 12% → 34%</span>
                <Link to="/case-studies/global-aluminum-lead-generation" className="text-xs font-bold text-[#2F5FE8] hover:text-[#2049BE] inline-flex items-center gap-1">
                  View Study <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>

            {/* Project 3: Centennial Family Foods */}
            <div className="reveal reveal-delay-2 group bg-white border border-[#E9E6DE] rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-[#2F5FE8]/5 hover:border-[#2F5FE8]/50 transition-all duration-300 flex flex-col justify-between lift-hover">
              <div className="p-6 md:p-8 flex flex-col gap-4 text-left">
                <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#2F5FE8]">Independent Grocery</div>
                <h3 className="text-xl md:text-2xl font-bold tracking-tight text-[#101319] group-hover:text-[#2F5FE8] transition-colors">Centennial Family Foods</h3>
                <p className="text-sm md:text-[14.5px] leading-relaxed text-[#5A6270]">
                  Replaced a paper-flyer model with a weekly digital distribution system via automated SMS + Email.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {['Digital Flyer', 'SMS Broadcast', 'Email List', 'Local SEO'].map((tag) => (
                    <span key={tag} className="text-[10px] md:text-[11px] font-mono-ibm bg-[#FBFAF7] border border-[#E9E6DE] px-2.5 py-1 rounded-full text-[#5A6270]">{tag}</span>
                  ))}
                </div>
              </div>
              <div className="px-6 md:px-8 pb-6 md:pb-8 flex justify-between items-center border-t border-[#E9E6DE]/60 pt-4 bg-[#FBFAF7]/40">
                <span className="text-xs font-bold text-[#127A50]">100% Weekly Automated</span>
                <Link to="/case-studies/centennial-family-foods-retail-system" className="text-xs font-bold text-[#2F5FE8] hover:text-[#2049BE] inline-flex items-center gap-1">
                  View Study <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ROI CALCULATOR ============ */}
      <section className="bg-[#F4F2EC] border-t border-[#E9E6DE] py-16 md:py-28 font-instrument">
        <div className="max-w-[1312px] mx-auto px-6 flex flex-col gap-12">
          <div className="flex flex-col gap-4 max-w-2xl text-left">
            <div className="reveal inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8]">
              <span className="w-6 h-[1px] bg-[#2F5FE8]"></span>
              See it in your numbers
            </div>
            <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold text-[#101319]">
              What are missed leads costing you?
            </h2>
            <p className="reveal reveal-delay-2 text-base md:text-lg leading-relaxed text-[#5A6270] mt-1 text-pretty">
              Most businesses don't lose jobs to a marketing problem. They lose them to slow follow-up. Put in your numbers and see the monthly leak.
            </p>
          </div>

          <div className="reveal reveal-delay-3 bg-white border border-[#E9E6DE] rounded-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-2 shadow-lg shadow-[#101319]/2 select-none lift-hover">
            {/* Inputs Panel */}
            <div className="p-8 md:p-12 flex flex-col gap-8 text-left justify-center">
              <label className="flex flex-col gap-3">
                <span className="flex justify-between text-sm md:text-base font-semibold text-[#5A6270]">
                  Missed / slow-handled leads per week 
                  <strong className="text-[#101319] font-bold text-base md:text-lg">{missed}</strong>
                </span>
                <input 
                  type="range" 
                  min="1" 
                  max="40" 
                  step="1" 
                  value={missed} 
                  onChange={(e) => setMissed(Number(e.target.value))} 
                  className="w-full h-1.5 bg-[#E9E6DE] rounded-lg appearance-none cursor-pointer accent-[#2F5FE8]" 
                />
              </label>

              <label className="flex flex-col gap-3">
                <span className="flex justify-between text-sm md:text-base font-semibold text-[#5A6270]">
                  Average job / deal value 
                  <strong className="text-[#101319] font-bold text-base md:text-lg">{valueFmt}</strong>
                </span>
                <input 
                  type="range" 
                  min="100" 
                  max="20000" 
                  step="100" 
                  value={value} 
                  onChange={(e) => setValue(Number(e.target.value))} 
                  className="w-full h-1.5 bg-[#E9E6DE] rounded-lg appearance-none cursor-pointer accent-[#2F5FE8]" 
                />
              </label>

              <label className="flex flex-col gap-3">
                <span className="flex justify-between text-sm md:text-base font-semibold text-[#5A6270]">
                  Close rate on leads you reach 
                  <strong className="text-[#101319] font-bold text-base md:text-lg">{close}%</strong>
                </span>
                <input 
                  type="range" 
                  min="5" 
                  max="80" 
                  step="5" 
                  value={close} 
                  onChange={(e) => setClose(Number(e.target.value))} 
                  className="w-full h-1.5 bg-[#E9E6DE] rounded-lg appearance-none cursor-pointer accent-[#2F5FE8]" 
                />
              </label>
              
              <div className="text-xs text-[#5A6270] font-medium">Estimate only, based on leads you already get but don't catch in time.</div>
            </div>

            {/* Calculations Output */}
            <div className="bg-[#101319] text-[#F2F3F5] p-8 md:p-12 flex flex-col justify-center gap-5 text-left">
              <div className="font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#9BA3B2]">Walking out the door</div>
              <div className="text-4xl sm:text-5xl md:text-[62px] font-bold tracking-tight text-white leading-none">{monthlyFmt}</div>
              <div className="text-base sm:text-lg text-[#9BA3B2]">
                in winnable revenue per month. That's <span className="text-white font-bold">{yearlyFmt} a year</span>.
              </div>
              <div className="flex flex-col sm:flex-row gap-4 mt-4">
                <Link to="/digital-audit" className="text-center bg-[#2F5FE8] hover:bg-[#2049BE] text-white font-bold px-6 py-3.5 rounded-xl shadow-md transition-all hover:scale-[1.02]">
                  Get my free audit
                </Link>
                <Link to="/book-consultation" className="text-center border border-white/20 hover:border-white/50 text-[#F2F3F5] font-bold px-6 py-3.5 rounded-xl transition-all hover:scale-[1.02]">
                  Book a 15-min call
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PROOF ============ */}
      <section id="proof" className="bg-[#101319] border-t border-[#1F222A] py-16 md:py-28 font-instrument scroll-mt-20">
        <div className="max-w-[1312px] mx-auto px-6 flex flex-col gap-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="flex flex-col gap-5 text-left">
              <div className="reveal inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#7C9EFF]">
                <span className="w-6 h-[1px] bg-[#7C9EFF]"></span>
                Founder-built proof
              </div>
              <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold text-white">
                Real systems. Real numbers.
              </h2>
              <p className="reveal reveal-delay-2 text-base md:text-[17px] leading-relaxed text-[#9BA3B2] text-pretty">
                Before Frayze, our founder spent four years rebuilding a 6-rooftop GTA dealer group from the database up. These are the numbers that became the Frayze blueprint.
              </p>
              
              <div className="reveal reveal-delay-3 border-l-2 border-[#7C9EFF] pl-6 py-1 flex flex-col gap-2 mt-1">
                <p className="text-base md:text-lg leading-relaxed text-[#F2F3F5] italic">"Frayze is that same playbook, productized so any business can run it."</p>
                <div className="text-xs md:text-sm text-[#B9C1CE] font-semibold">Denis Mahoney · Founder, Frayze</div>
              </div>
              
              <Link to="/case-studies" className="text-sm md:text-base font-bold text-[#7C9EFF] hover:text-[#A9BFF7] transition-colors mt-2 inline-flex items-center gap-1.5">
                Read the full case studies <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-0.5 bg-[#1F222A] border border-[#1F222A] rounded-2xl overflow-hidden shadow-sm">
              <div className="reveal bg-[#161920] p-8 md:p-10 flex flex-col gap-2 text-left lift-hover">
                <div className="text-3xl sm:text-4xl md:text-[48px] font-bold tracking-tight text-[#7C9EFF]">400%</div>
                <div className="text-xs md:text-sm text-[#9BA3B2] font-semibold">Lead growth</div>
              </div>
              <div className="reveal reveal-delay-1 bg-[#161920] p-8 md:p-10 flex flex-col gap-2 text-left lift-hover">
                <div className="text-3xl sm:text-4xl md:text-[48px] font-bold tracking-tight text-[#7C9EFF]">$1.7M</div>
                <div className="text-xs md:text-sm text-[#9BA3B2] font-semibold">Saved in annual marketing spend</div>
              </div>
              <div className="reveal reveal-delay-2 bg-[#161920] p-8 md:p-10 flex flex-col gap-2 text-left lift-hover">
                <div className="text-3xl sm:text-4xl md:text-[48px] font-bold tracking-tight text-[#7C9EFF]">47%</div>
                <div className="text-xs md:text-sm text-[#9BA3B2] font-semibold">Sales increase</div>
              </div>
              <div className="reveal reveal-delay-3 bg-[#161920] p-8 md:p-10 flex flex-col gap-2 text-left lift-hover">
                <div className="text-3xl sm:text-4xl md:text-[48px] font-bold tracking-tight text-[#7C9EFF]">52%</div>
                <div className="text-xs md:text-sm text-[#9BA3B2] font-semibold">Lower tech costs</div>
              </div>
            </div>
          </div>

          <div className="border-t border-[#1F222A] pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="font-mono-ibm text-[10px] tracking-[0.14em] uppercase text-[#9BA3B2] font-semibold shrink-0">Powered by Frayze</div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm md:text-[15px] font-bold text-[#9BA3B2] select-none text-left">
              <span>Aluminum Solutions</span>
              <span>Monty's Joint</span>
              <span>Johnny Avella</span>
              <span>Kovara</span>
              <span>Gordon Trailer Sales</span>
              <span>Urbis Concepts</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ GROWTH LADDER ============ */}
      <section className="bg-[#F4F2EC] border-t border-[#E9E6DE] py-16 md:py-28 font-instrument">
        <div className="max-w-[1312px] mx-auto px-6 flex flex-col gap-12">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6">
            <div className="flex flex-col gap-4 max-w-xl text-left">
              <div className="reveal inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8]">
                <span className="w-6 h-[1px] bg-[#2F5FE8]"></span>
                The growth ladder
              </div>
              <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold text-[#101319]">
                Start with the core. Scale when you're ready.
              </h2>
            </div>
            <Link to="/pricing" className="reveal reveal-delay-2 text-sm md:text-base font-bold text-[#2F5FE8] hover:text-[#2049BE] whitespace-nowrap inline-flex items-center gap-1">
              See full pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-left">
            <Link to="/website-launch-program" className="reveal bg-white border border-[#E9E6DE] hover:border-[#2F5FE8] hover:shadow-md rounded-2xl p-6 md:p-7 flex flex-col gap-3 transition-all duration-300 group lift-hover">
              <div className="font-mono-ibm text-xs text-[#2F5FE8] font-bold">01</div>
              <h3 className="text-lg md:text-[19px] font-bold tracking-tight text-[#101319]">Website &amp; CRM Core</h3>
              <p className="text-xs md:text-sm leading-relaxed text-[#5A6270]">Get online with a high-converting site and reliable CRM.</p>
              <div className="mt-8 text-[13px] md:text-[13.5px] font-bold text-[#5A6270] group-hover:text-[#101319] transition-colors">Fixed-quote entry build</div>
            </Link>

            <Link to="/pricing" className="reveal reveal-delay-1 bg-white border border-[#E9E6DE] hover:border-[#2F5FE8] hover:shadow-md rounded-2xl p-6 md:p-7 flex flex-col gap-3 transition-all duration-300 group lift-hover">
              <div className="font-mono-ibm text-xs text-[#2F5FE8] font-bold">02</div>
              <h3 className="text-lg md:text-[19px] font-bold tracking-tight text-[#101319]">Managed CRM</h3>
              <p className="text-xs md:text-sm leading-relaxed text-[#5A6270]">We run your follow-up. Nothing slips through.</p>
              <div className="mt-8 text-[13px] md:text-[13.5px] font-bold text-[#5A6270] group-hover:text-[#101319] transition-colors">Scoped to your business</div>
            </Link>

            <Link to="/pricing" className="reveal reveal-delay-2 bg-white border border-[#E9E6DE] hover:border-[#2F5FE8] hover:shadow-md rounded-2xl p-6 md:p-7 flex flex-col gap-3 transition-all duration-300 group lift-hover">
              <div className="font-mono-ibm text-xs text-[#2F5FE8] font-bold">03</div>
              <h3 className="text-lg md:text-[19px] font-bold tracking-tight text-[#101319]">Growth Engine</h3>
              <p className="text-xs md:text-sm leading-relaxed text-[#5A6270]">Predictable inbound + outbound demand, without ads.</p>
              <div className="mt-8 text-[13px] md:text-[13.5px] font-bold text-[#5A6270] group-hover:text-[#101319] transition-colors">Scoped to your growth stage</div>
            </Link>

            <Link to="/pricing" className="reveal reveal-delay-3 bg-white border border-[#E9E6DE] hover:border-[#2F5FE8] hover:shadow-md rounded-2xl p-6 md:p-7 flex flex-col gap-3 transition-all duration-300 group lift-hover">
              <div className="font-mono-ibm text-xs text-[#2F5FE8] font-bold">04</div>
              <h3 className="text-lg md:text-[19px] font-bold tracking-tight text-[#101319]">Scale System</h3>
              <p className="text-xs md:text-sm leading-relaxed text-[#5A6270]">Google &amp; Meta ads managed on a proven system.</p>
              <div className="mt-8 text-[13px] md:text-[13.5px] font-bold text-[#5A6270] group-hover:text-[#101319] transition-colors">Scoped to your ad budget</div>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ GRANT BANNER ============ */}
      <section className="bg-[#F4F2EC] px-6 pb-16 md:pb-28 font-instrument">
        <div className="reveal bg-gradient-to-br from-[#EEF2FE] to-white border border-[#DCE3F8] rounded-2xl p-8 md:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 text-left hover:shadow-sm transition-all duration-300 lift-hover max-w-[1312px] mx-auto">
          <div className="flex flex-col gap-2.5 max-w-3xl">
            <div className="font-mono-ibm text-[10px] md:text-xs tracking-[0.14em] uppercase text-[#2F5FE8] font-bold">BBAA AI-adoption grant · Northwestern Ontario</div>
            <h3 className="text-2xl sm:text-3xl md:text-[28px] font-bold tracking-[-0.02em] leading-tight text-[#101319]">Your AI project may qualify for up to $20,000</h3>
            <p className="text-sm md:text-[15.5px] leading-relaxed text-[#5A6270]">NOIC's Building Blueprints for AI Adoption program can cover 50% of a new, eligible AI project for Northwestern Ontario businesses. Funding is limited and the program decides eligibility. Frayze scopes the project, builds it, and helps with the application.</p>
          </div>
          <Link to="/book-consultation" className="bg-[#101319] hover:bg-[#2F5FE8] text-white font-semibold text-sm md:text-[15px] px-6 py-3.5 rounded-xl transition-all hover:scale-[1.02] shrink-0 w-full sm:w-auto text-center shadow-sm">
            See if you qualify
          </Link>
        </div>
      </section>

      {/* ============ FAQ SECTION (SEO Essential Preservation) ============ */}
      <section className="bg-white border-t border-[#E9E6DE] py-16 md:py-28 font-instrument">
        <div className="max-w-[860px] mx-auto px-6 flex flex-col gap-12 text-left">
          <div className="flex flex-col gap-4 text-center max-w-2xl mx-auto">
            <div className="reveal inline-flex items-center justify-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8] mx-auto">
              <span className="w-6 h-[1px] bg-[#2F5FE8]"></span>
              Questions
            </div>
            <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[44px] leading-[1.04] tracking-[-0.035em] font-semibold text-[#101319]">
              Common Questions
            </h2>
          </div>

          <div className="flex flex-col gap-3">
            {homeFaqs.map((faq, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <div
                  key={index}
                  className="reveal border border-[#E9E6DE] rounded-2xl bg-[#FBFAF7]/50 hover:bg-[#FBFAF7] transition-all overflow-hidden"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex justify-between items-center p-5 text-left font-bold text-[#101319] text-base md:text-lg gap-4"
                    aria-expanded={isOpen}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown className={`w-5 h-5 text-[#5A6270] shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#2F5FE8]' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="p-5 pt-0 border-t border-[#E9E6DE]/60 text-sm md:text-base leading-relaxed text-[#5A6270] animate-fadeUp">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="reveal text-center text-sm md:text-base leading-relaxed text-[#5A6270] mt-2">
            In Thunder Bay? Start with our dedicated page for <Link to="/service-areas/thunder-bay/" className="font-bold underline decoration-[#2F5FE8]/50 hover:text-[#2F5FE8] transition-colors">Thunder Bay website design and SEO</Link>.
          </p>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-gradient-to-br from-[#2F5FE8] to-[#1E43B3] border-t border-[#2F5FE8] py-16 md:py-32 text-center font-instrument">
        <div className="max-w-[800px] mx-auto px-6 flex flex-col items-center gap-6">
          <div className="reveal font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#A9BFF7] font-bold">Ready to start?</div>
          <h2 className="reveal reveal-delay-1 text-4xl sm:text-5xl md:text-[58px] leading-[1.02] tracking-[-0.04em] font-semibold text-white">
            Start with a modern website + AI business system
          </h2>
          <p className="reveal reveal-delay-2 text-base md:text-lg leading-relaxed text-[#D2DEFC] max-w-xl">
            Lead capture, automation, AI response, and CRM structure, built in from day one. No contracts.
          </p>
          <div className="reveal reveal-delay-3 flex flex-col sm:flex-row items-center gap-4 mt-4 w-full sm:w-auto">
            <Link to="/website-launch-program" className="w-full sm:w-auto text-center bg-white hover:bg-[#FBFAF7] text-[#2F5FE8] font-bold px-8 py-4 rounded-xl shadow-lg shadow-black/10 transition-all hover:scale-[1.02]">
              View Launch Program
            </Link>
            <Link to="/book-consultation" className="w-full sm:w-auto text-center border border-white/30 hover:border-white text-white bg-transparent hover:bg-white/10 font-bold px-8 py-4 rounded-xl transition-all hover:scale-[1.02]">
              Book a Free Call
            </Link>
          </div>
          <GoogleReviewsBadge tone="dark" className="reveal reveal-delay-3 mt-2" />
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;